import { useEffect, useRef, useState } from "react";
import "./AppEmployeeRecordNew.css";
import { 
    AppGenericProps,
    CreateEmployeeRecordPayload,
    CustomProperty,
    CustomPropertyType,
    CustomPropertyValue,
    EmployeeRecord,
    EmployeeType,
    FileToCloudURL, 
    formatDateDayMonDDYYYY, 
    PaidTimeOffAdjustment,
    PaidTimeOffPolicy,
    TimeOffRequest
} from "winslow-core";
import { AppEmployeeRecordTypeSelector } from "./AppEmployeeRecordTypeSelector";
import { AppEmployeeRecordForm } from "./AppEmployeeRecordForm";
import { AppChanges } from "../app-changes/AppChanges";
import { useNavigate } from "react-router-dom";
import { EmployeeRecordSelect } from "../../shared/EmployeeRecordSelect";
import { DEFAULT_NEW_EMPLOYEE_RECORD, EmployeeTypeDisplayNames, ESSENTIAL_DESCRIPTIONS } from "./employeeRecordConfig";
// import { PropertyDefinitionSelect } from "../../shared/PropertyDefinitionSelect";
// import { DaysOfWork } from "./DaysOfWork";
// import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
// import { RadioButtons } from "./RadioButtons";
// import { SalaryType } from "winslow-core";
import { 
    apiCreateEmployeeRecord,
    apiGetCustomPropertiesForWorkspace,
    apiGetEmployeeRecord,
    // apiGetPropertiesForWorkspace,
    apiUpdateEmployeeRecord
} from "../../../services/EmployeeRecordService";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/features/appData";
import toast from "react-hot-toast";
import { AppCustomProperty } from "../app-customize/AppCustomProperty";
import { postImage, postMultipleEmployeeFileUpload, postMultipleFileUpload } from "../../../services/client";
import { toastFail, toastSuccess } from "../../../config";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
// import { PropertyDefinition } from "winslow-core";
// import { EmployeeRecordAddress } from "./EmployeeRecordAddress";
// import { EmployeeRecordDaysOfWork } from "./EmployeeRecordDaysOfWeek";
import { cpGetDropdownChoices, erGetCPValue, erIsValid, erSetCPValue } from "./employeeRecordUtils"
import { validateEmail } from "../../uiHelpers";
import { Check, CircleAlert, Upload, View } from "lucide-react";
import { set } from "lodash";
import ModalConfirmChangeType from "./ModalConfirmChangeType";
import { apiGetPaidTimeOffAdjustment, apiGetPaidTimeOffAdjustmentsForEmployeeRecord } from "../../../services/PaidTimeOffAdjustmentService";
import { apiGetPaidTimeOffPoliciesForWorkspace, apiGetPaidTimeOffPolicy } from "../../../services/PaidTimeOffPolicyService";
import { apiGetTimeOffRequestsForEmployeeRecord } from "../../../services/TimeOffRequestService";

const datePickerFormat = "MM/dd/yyyy";

type AppEmployeeRecordNewProps = {
    id: string | undefined;  // undefined === new
    genericProps?: AppGenericProps;
}

export const AppEmployeeRecordNew = ({id, genericProps} : AppEmployeeRecordNewProps) => {

    const storeUser = useSelector(selectUser);

    const navigate = useNavigate();
    
    const [employeeType, setEmployeeType] = useState<EmployeeType | undefined>(undefined);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [newEmployeeRecord, setNewEmployeeRecord] = useState<Partial<EmployeeRecord>>(DEFAULT_NEW_EMPLOYEE_RECORD);

    const [propertyDefinitions, setPropertyDefinitions] = useState<PropertyDefinition[] | undefined>(undefined);
    const [customProperties, setCustomProperties] = useState<CustomProperty[] | undefined>(undefined);
    const [newCustomProperties, setNewCustomProperties] = useState<Map<string, any>>(new Map());
    const [newCustomFilesURLs, setNewCustomFilesURLs] = useState<Map<string, FileToCloudURL[]>>(new Map());

    const [validate, setValidate] = useState<boolean>(false);

    const [validateChangeType, setValidateChangeType] = useState<boolean>(false);
    const [changeTypeTo, setChangeTypeTo] = useState<EmployeeType | undefined>(undefined);

    const [avatarFile, setAvatarFile] = useState<string>("");
    const avatarFileRef = useRef(null);
    
    const [paidTimeOffAdjustments, setPaidTimeOffAdjustments] = useState<PaidTimeOffAdjustment[] | undefined>(undefined);
    const [showAdjustments, setShowAdjustments] = useState<boolean>(false);
    const [paidTimeOffPolicies, setPaidTimeOffPolicies] = useState<PaidTimeOffPolicy[] | undefined>(undefined);

    const [showTimeOffRequests, setShowTimeOffRequests] = useState<boolean>(false);
    const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[] | undefined>(undefined);

    const containerRef = useRef(null);

    useEffect(() => {
        

        // if (!propertyDefinitions) {
        //     (async () => {
        //         const pd = await apiGetPropertiesForWorkspace(storeUser?.currentWorkspaceId || "");

        //         if (!pd) {
        //             toast("Error getting property definitions");
        //             return;
        //         }

        //         setPropertyDefinitions(pd);
        //     })();
        // }

        if (id) {
            // Load existing employee record
            (async () => {
                const er = await apiGetEmployeeRecord(id);
                
                if (!er) {
                    toast("Error getting employee record", toastFail);
                    return;
                }

                setNewEmployeeRecord(er);
                setEmployeeType(er.employeeType);

                const existingCustomProperties = new Map<string, any>();

                if (er.customProperties) {
                    const theCustomProperties = JSON.parse(er.customProperties || "[]") as CustomPropertyValue[];
                    theCustomProperties.forEach((cp) => {
                        existingCustomProperties.set(cp.label, cp.data);
                    });
                }       

                setNewCustomProperties(existingCustomProperties);

                // get this non blocking
                apiGetPaidTimeOffAdjustmentsForEmployeeRecord(er.id).then((adjustments) => {
                    if (!adjustments) {
                        toast("Error getting PTO adjustments", toastFail);
                        return;
                    }

                    setPaidTimeOffAdjustments(adjustments);
                });

                apiGetPaidTimeOffPoliciesForWorkspace(storeUser?.currentWorkspaceId || "").then((policies) => {
                    if (!policies) {
                        toast("Error getting PTO policies", toastFail);
                        return;
                    }

                    setPaidTimeOffPolicies(policies);
                });

                apiGetTimeOffRequestsForEmployeeRecord(er.id).then((requests) => {
                    if (!requests) {
                        toast("Error getting time off requests", toastFail);
                        return;
                    }

                    setTimeOffRequests(requests);
                });
                
            })();
        }
    }, []);

    useEffect(() => {
        if (!storeUser?.currentWorkspaceId) {
            return;
        }

        if (!customProperties && employeeType) {
            (async () => {
                const cp = await apiGetCustomPropertiesForWorkspace(storeUser?.currentWorkspaceId || "");

                if (!cp) {
                    toast("Error getting custom properties");
                    return;
                }

                const cpByType = cp.filter((customProperty: CustomProperty) => {
                    return customProperty.visibleEmployeeTypes.includes(employeeType)
                });

                setCustomProperties(cpByType);
            })();
        }
    }, [storeUser, employeeType]);

    useEffect(() => {
        console.log("[AppEmployeeRecordNew/ue] new employee record: ", newEmployeeRecord);
    }, [newEmployeeRecord]);

    useEffect(() => {
        console.log("[AppEmployeeRecordNew/ue] new custom property: ", newCustomProperties);
    }, [newCustomProperties]);

    // const dateDayMonDDYYYY = (date: Date) => {
    //     const options : any = {
    //         weekday: 'short',
    //         month: 'short',
    //         day: '2-digit',
    //         year: 'numeric',
    //     };
        
    //     const formattedDate = date.toLocaleDateString('en-US', options);
    //     return formattedDate;
    // }

    // const handleCustomPropertyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     console.log("[AppEmployeeRecordNew] field: ", event.target.name);
    //     console.log("[AppEmployeeRecordNew] value: ", event.target.value);
    //     setHasChanges(true);

    //     const newRecord = {...newEmployeeRecord};

    //     setNewEmployeeRecord({
    //         ...newRecord,
    //         customProperties: erSetCPValue(newRecord, event.target.name, event.target.value)
    //     })
    // }

    const handleSubmit = async () => {

        if (!employeeType) {   
            toast("Please select an employee type", toastFail);
            return;
        }

        setValidate(true);
        console.log("[AppEmployeeRecordNew/handleSubmit] newCustomProperties: ", newCustomProperties);

        const arrayOfObjects = Array.from(newCustomProperties, ([key, value]) => ({ label: key, data: value }));

        const cpsByType = customProperties?.filter((customProperty: CustomProperty) => {
            return customProperty.visibleEmployeeTypes.includes(employeeType)
        });

        const isValid = erIsValid({
            ...newEmployeeRecord, 
            customProperties: JSON.stringify(arrayOfObjects)
        }, cpsByType || []);

        console.log("[AppEmployeeRecordNew/handleSubmit] is valid: ", isValid);

        if (!isValid) {
            toast("Please fill in all required fields and formats", toastFail);
            return;
        }

        // if this is an update, revector to that
        if (id) {
            updateEmployeeRecord();
            return;
        }

        // 1. Upload all customer files and get URLs
        setNewCustomFilesURLs(new Map<string, FileToCloudURL[]>());
        const updatedCustomFilesURLs = new Map<string, FileToCloudURL[]>(newCustomFilesURLs);

        for (let [key, value] of newCustomProperties) {

            if (value instanceof FileList) {
                console.log("Uploading files for: ", key);
                const fileURLs = await uploadFilesForCollection(value);

                // console.log("[AddEmployeeRecordNew] fileURLs: ", fileURLs);

                if (!fileURLs) {
                    toast("Error uploading files", toastFail);
                    continue;
                }

                updatedCustomFilesURLs.set(key, fileURLs);
            }
        }

        setNewCustomFilesURLs(updatedCustomFilesURLs);
        // console.log("[AppEmployeeRecordNew] updated custom files URLs: ", updatedCustomFilesURLs);

        // 3. Create new employee record object
        const payload : CreateEmployeeRecordPayload = {
            workspaceId: storeUser?.currentWorkspaceId || "",
            essentials: newEmployeeRecord,
            customProperties: JSON.stringify(convertPropertyMapToArray(newCustomProperties, updatedCustomFilesURLs)),
        }

        console.log("[AppEmployeeRecordNew] payload: ", payload);
        // return;

        const employeeRecord = await apiCreateEmployeeRecord(payload);

        if (!employeeRecord) {
            toast("Error creating employee", toastFail);
            return;
        }

        toast("Employee created", toastSuccess);
        navigate("/app/employee-records");
    }

    const handleCancel = () => {
        navigate("/app/employee-records");
    }

    const convertPropertyMapToArray = (map: Map<string, any>, uploadedFiles: Map<string, FileToCloudURL[]>) => {
        const array: any[] = [];

        for (let [key, value] of map) {
            if (value instanceof FileList) {
                if (uploadedFiles.has(key)) {
                    array.push({
                        label: key,
                        data: uploadedFiles.get(key),
                    });
                }
            } else {
                array.push({
                    label: key,
                    data: value,
                });
            }
        }

        return array;
    }

    const updateEmployeeRecord = async () => {
        // 0. figure out if any customer properties are new files and then upload them
        // NR**>

        // 1. put custom props into the data object and go
        const payload : Partial<EmployeeRecord> = {
            ...newEmployeeRecord,
            customProperties: JSON.stringify(convertPropertyMapToArray(newCustomProperties, new Map<string, FileToCloudURL[]>())),
        }

        const updatedEmployeeRecord = await apiUpdateEmployeeRecord(payload);

        if (!updatedEmployeeRecord) {
            toast("Error updating employee", toastFail);
            return;
        }

        setNewEmployeeRecord(updatedEmployeeRecord);

        const existingCustomProperties = new Map<string, any>();

        if (updatedEmployeeRecord.customProperties) {
            const theCustomProperties = JSON.parse(updatedEmployeeRecord.customProperties || "{}") as CustomPropertyValue[];

            if (Object.keys(theCustomProperties).length !== 0) {
                theCustomProperties.forEach((cp) => {
                    existingCustomProperties.set(cp.label, cp.data);
                });
            }
        }       

        setNewCustomProperties(existingCustomProperties);
        setHasChanges(false);
        toast("Employee updated", toastSuccess);
    }


    const uploadFilesForCollection = async (uploadFiles: FileList) => {

        if (!storeUser?.currentWorkspaceId) {
            toast("Error uploading files, no workspace id", toastFail);
            return;
        }
        
        toast("Uploading " + uploadFiles.length + " files...", toastSuccess);

        try {
            const response = await postMultipleEmployeeFileUpload(
              storeUser?.currentWorkspaceId,
              uploadFiles,
            );

            if (!response) {
              toast("Error uploading files ", toastFail);
              return null;
            }

            console.log("[AppEmployeeRecordNew] postMultiple response: ", response);

            return response.data.data;

        } catch (error) {
            // Handle error
            toast("Error uploading file, please try again.", toastFail);
            return null;
        }

    };

    const etOptions = Object.keys(EmployeeType).map((key) => {

        const index = key as keyof typeof EmployeeType;

        return {
            displayName: EmployeeTypeDisplayNames[index],
            value: key,
            image: undefined,
            component: undefined,
        }
    });

    const changeAvatar = () => {
        if (avatarFileRef?.current) {
            const inputButton = avatarFileRef.current as any;
            setNewEmployeeRecord({...newEmployeeRecord, avatarURL: ""});
            setAvatarFile("");
            inputButton.click();
        }
    };

    const handleFileChange = (event: any) => {

        if (!storeUser) {
            return;
        }
        
        if (event.target.files && event.target.files?.length < 1) {
          return;
        }
    
        if (event.target.files && event.target.files[0].size > 10485760) {
          toast("Avatar is too big! Please keep it under 10MB", toastFail);
          return;
        }
    
        const theFile = event.target.files ? event.target.files[0] : null;
    
        if (!theFile) {
          toast("Did not get a valid file", toastFail);
          return;
        }
    
        (async () => {
          toast("Uploading...", toastSuccess);
    
          try {
            const response = await postImage(storeUser.id, theFile, ()=>{});

            if (!response) {
                toast("Error uploading file", toastFail);
                return;
            }

            setNewEmployeeRecord({...newEmployeeRecord, avatarURL: response.data.data});
    
            // if (response.status === 200) {
            //   const theUser = {
            //     ...settingsData.user,
            //     avatarURL: response.data.data,
            //   };
            //   setSettingsData({ ...settingsData, user: theUser });
    
            //   const apiUser = await apiSaveUser(theUser);
    
            //   if (!apiUser) {
            //     toast("Could not save user, please try again.", toastFail);
            //     return;
            //   }
    
            //   dispatch(setUser(apiUser));
            //   reloadAll(storeUser?.currentWorkspaceId);
            // } else {
            //   toast("File not uploaded: " + response.status, toastFail);
            // }
          } catch (error) {
            // Handle error
            toast("Error uploading file, please try again.", toastFail);
          }
        })();
    };

    // if someoe change the employee type, reset the custom properties that do not apply
    // to the employee type to null, blank, zero as it may confuse the system to have 
    // data in the DB

    const resetEmployeeRecordCustomProperties = (employeeType: EmployeeType) => {
        const updatedCustomProperties = new Map(newCustomProperties);
        const resetEmployeeRecordFields : any = { };

        customProperties?.forEach((customProperty: CustomProperty) => {
            if (!customProperty.visibleEmployeeTypes.includes(employeeType)) {
                const data : any = "";

                switch (customProperty.type) {
                    case CustomPropertyType.SHORT:
                    case CustomPropertyType.LONG:
                    case CustomPropertyType.RICH:
                    case CustomPropertyType.PHONE:
                    case CustomPropertyType.EMAIL:
                    case CustomPropertyType.DATE:
                    case CustomPropertyType.CHECKBOX_SINGLE:
                    case CustomPropertyType.DROPDOWN:
                    case CustomPropertyType.RADIO:
                    case CustomPropertyType.FILE:
                        resetEmployeeRecordFields[customProperty.label] = "";  
                        break;
                    case CustomPropertyType.NUMBER:
                        resetEmployeeRecordFields[customProperty.label] = 0;  
                        break;
                    case CustomPropertyType.CHECKBOX_MULTIPLE:
                        resetEmployeeRecordFields[customProperty.label] = []  
                        break;
                    case CustomPropertyType.EMPLOYEE_RECORD:
                    case CustomPropertyType.PTO_POLICIES:
                        resetEmployeeRecordFields[customProperty.label] = undefined;  
                        break;
                    default:
                        resetEmployeeRecordFields[customProperty.label] = data;
                        break;
                }
            }
        });

        console.log("[AppEmployeeRecordNew] changing type to: ", employeeType);
        console.log("[AppEmployeeRecordNew] reset employee record fields: ", resetEmployeeRecordFields);

        setNewEmployeeRecord({
            ...newEmployeeRecord,
            employeeType,
            ...resetEmployeeRecordFields,
        });
    }

    // const officeChoices = cpGetDropdownChoices(customProperties, "system-office");
    // const officeOptions = officeChoices ? officeChoices.map((choice: string) => {
    //     return {
    //         displayName: choice,
    //         value: choice,
    //         image: undefined,
    //         component: undefined,
    //     } as GenericSelectOption
    // }) : [] as GenericSelectOption[];

    // const departmentChoices = cpGetDropdownChoices(customProperties, "system-department");
    // const departmentOptions = departmentChoices ? departmentChoices.map((choice: string) => {
    //     return {
    //         displayName: choice,
    //         value: choice,
    //         image: undefined,
    //         component: undefined,
    //     } as GenericSelectOption
    // }) : [] as GenericSelectOption[];

    // const designationChoices = cpGetDropdownChoices(customProperties, "system-designation");
    // const designationOptions = designationChoices ? designationChoices.map((choice: string) => {
    //     return {
    //         displayName: choice,
    //         value: choice,
    //         image: undefined,
    //         component: undefined,
    //     }
    // }) : [] as GenericSelectOption[];

    // console.log("[AppEmployeeRecordNew] id: ", id);
    // console.log("[AppEmployeeRecordNew] new employee record: ", newEmployeeRecord);
    // console.log("[AppEmployeeRecordNew] new custom properties: ", newCustomProperties);

    console.log("[AppEmployeeRecordNew] etOptions: ", etOptions);
    console.log("[AppEmployeeRecordNew] id: ", id);

    return (
        <div 
            className="app-employee-record-new"
            ref={containerRef}
        >
            {!employeeType &&
                <AppEmployeeRecordTypeSelector 
                    onSubmit={(employeeType: EmployeeType) => {
                        

                        setEmployeeType(employeeType);
                        setNewEmployeeRecord({
                            ...newEmployeeRecord,
                            employeeType,
                        });
                        
                    }}
                />
            }

            {employeeType && id &&
                <div className="app-employee-record-additional-info">
                    <div className="app-employee-record-left">
                        {}
                    </div>
                    <div className="app-employee-record-additional-item">
                        <a 
                            onClick={() => {
                                setShowAdjustments(!showAdjustments);
                            }}
                        >
                            PTO Adjustments
                        </a>
                        <a 
                            onClick={() => {
                                setShowTimeOffRequests(!showTimeOffRequests);
                            }}
                        >
                            Time Off Requests
                        </a>
                    </div>
                </div>
            }

            {showAdjustments && employeeType && id && 
                <>
                    {!paidTimeOffAdjustments || paidTimeOffAdjustments.length < 1 &&
                        <>
                            No PTO Adjustments
                        </>
                    }
                    {paidTimeOffAdjustments && paidTimeOffAdjustments.length > 0 && paidTimeOffPolicies &&
                        <div className="app-employee-record-pto-adjustments">
                            {paidTimeOffAdjustments.map((adjustment: PaidTimeOffAdjustment) => {
                                const policy = paidTimeOffPolicies.find((policy: PaidTimeOffPolicy) => {
                                    return policy.id === adjustment.paidTimeOffPolicyId;
                                });

                                if (!policy) {
                                    return null;
                                }

                                return (
                                    <div className="app-employee-record-pto-adjustment">
                                        Policy: {policy.name} <br/>
                                        Adjustment Date: {formatDateDayMonDDYYYY(new Date(adjustment.createdAt))} <br/>
                                        adjustedHours: {adjustment.isAddition ? "" : "-"}{Math.floor(adjustment.adjustedHours/8)} Days<br/>
                                        <pre>
                                            {adjustment.noteVisible}
                                        </pre>
                                        <pre>
                                            {adjustment.noteAdmin}
                                        </pre>
                                    </div>
                                )
                            })}
                        </div>
                    }
                </>
                
            }

            {showTimeOffRequests && employeeType && id &&
                <>
                    {!timeOffRequests || timeOffRequests.length < 1 &&
                        <>
                            No time off requests
                        </>
                    }
                    {timeOffRequests && timeOffRequests.length > 0 && paidTimeOffPolicies &&
                        <div className="app-employee-record-time-off-requests">
                            {timeOffRequests.map((tor: TimeOffRequest) => {
                                const policy = paidTimeOffPolicies.find((policy: PaidTimeOffPolicy) => {
                                    return policy.id === tor.paidTimeOffPolicyId;
                                });

                                if (!policy) {
                                    return null;
                                }

                                return (
                                    <div className="app-employee-record-time-off-request">
                                        Policy: {policy.name} <br/>
                                        Start Date: {formatDateDayMonDDYYYY(new Date(tor.startDate))} <br/>
                                        End Date: {formatDateDayMonDDYYYY(new Date(tor.endDate))} <br/>
                                        Status: {tor.status} <br/>
                                    </div>
                                )
                            })}
                        </div>
                    }
                </>
            }

            {employeeType &&
                <>
                    <div className="app-employee-record-essentials">

                        <div className="app-employee-record-property">
                            <div className="app-employee-record-left">
                                <div className="app-employee-record-label">
                                    Employee Type
                                </div>
                            </div>
                            <div className="app-employee-record-middle">
                                <div className="app-employee-record-value">
                                    <GenericSelect
                                        options={etOptions}
                                        selected={etOptions.find((option) => option.value === employeeType)}
                                        onChange={(value: EmployeeType) => {
                                            if (id !== "new") {
                                                setValidateChangeType(true);
                                                setChangeTypeTo(value);
                                                return;
                                            }

                                            setEmployeeType(value);
                                            setNewEmployeeRecord({
                                                ...newEmployeeRecord,
                                                employeeType: value,
                                            })
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="app-employee-record-right">
                                {ESSENTIAL_DESCRIPTIONS.employeeType}
                            </div>
                        </div>

                        {(employeeType === EmployeeType.US_FULL_TIME || employeeType === EmployeeType.US_PART_TIME) &&
                            <div className="app-employee-record-property">
                                <div className="app-employee-record-left">
                                    <div className="app-employee-record-label">
                                        Profile Picture
                                    </div>
                                </div>
                                <div className="app-employee-record-middle">
                                    <div className="app-employee-record-value">
                                        <div className="app-employee-record-avatar">
                                            {newEmployeeRecord.avatarURL &&
                                                <img src={newEmployeeRecord.avatarURL} alt="Profile"/>
                                            }
                                            <input
                                                type="file"
                                                accept="image/png, image/jpeg, image/jpg, image/gif"
                                                ref={avatarFileRef}
                                                style={{ display: "none" }}
                                                value={avatarFile}
                                                onChange={handleFileChange}
                                                onError={(event) => console.log("onError", event)}
                                            />
                                            <Upload 
                                                size={24}
                                                onClick={changeAvatar}
                                                style={{cursor: "pointer"}}

                                            />
                                            {/* <button
                                                className="button-save"
                                                onClick={changeAvatar}

                                            >
                                                Upload Profile Picture
                                            </button> */}
                                        </div>
                                    
                                    </div>
                                </div>
                                
                                <div className="app-employee-record-right">
                                    {ESSENTIAL_DESCRIPTIONS.workEmail}
                                </div>
                            </div>
                        }

                        <div className="app-employee-record-property">
                            <div className="app-employee-record-left">
                                <div className="app-employee-record-label">
                                    {(employeeType === EmployeeType.US_FULL_TIME || 
                                        employeeType === EmployeeType.US_PART_TIME) ? "Work Email" : "Contact Email"
                                    }
                                </div>
                            </div>
                            <div className="app-employee-record-middle">
                                <div className="app-employee-record-value">
                                    <input
                                        type="text"
                                        value={newEmployeeRecord.workEmail}
                                        name="workEmail"
                                        onChange={(e: any) => 
                                            setNewEmployeeRecord({
                                                ...newEmployeeRecord,
                                                workEmail: e.target.value
                                            })
                                        }
                                    />
                                    {validate && !newEmployeeRecord.workEmail &&
                                        <div className="app-employee-record-error">
                                            <Check size={16} color="red"/>
                                        </div>
                                    }
                                    {validate && newEmployeeRecord.workEmail && 
                                        !validateEmail(newEmployeeRecord.workEmail) &&
                                        <div className="app-employee-record-error">
                                            <CircleAlert size={16} color="red"/>
                                        </div>
                                    }
                                </div>
                            </div>
                            
                            <div className="app-employee-record-right">
                                {ESSENTIAL_DESCRIPTIONS.workEmail}
                            </div>
                        </div>
                    </div>

                    <div className="app-employee-record-custom">
                        {customProperties?.filter((customProperty: CustomProperty) => {
                                return customProperty.visibleEmployeeTypes.includes(employeeType)
                            })
                            .map((customProperty: CustomProperty, index: number) => {
                                return (
                                    
                                    <div className="app-employee-record-property"> 
                                        <div className="app-employee-record-left">
                                            <div className="app-employee-record-label">
                                                {customProperty.display}
                                            </div>
                                            {!customProperty.required &&
                                                <div className="app-employee-record-optional">
                                                    OPTIONAL
                                                </div>
                                            }
                                        </div>
                                        <div className="app-employee-record-middle">
                                            <AppCustomProperty
                                                key={"cp-" + index}
                                                customProperty={customProperty}
                                                data={newCustomProperties.get(customProperty.label) || undefined}
                                                validate={validate}
                                                onChange={(label: string, data: any) => {
                                                    const updatedCustomProperties = new Map(newCustomProperties);
                                                    updatedCustomProperties.set(label, data);
                                                    setNewCustomProperties(updatedCustomProperties);
                                                    setHasChanges(true);
                                                }}
                                            />
                                        </div>
                                        <div className="app-employee-record-right">
                                            {customProperty.description}
                                        </div>
                                    </div>

                                )
                            })
                        }
                    </div>
                    <div style={{paddingBottom: "200px"}}>{ " "}</div>
                </>

            }

            {validateChangeType && changeTypeTo &&
                <ModalConfirmChangeType
                    isOpen={validateChangeType}
                    onClose={() => {
                        setValidateChangeType(false)
                        setChangeTypeTo(undefined);
                    }}
                    onContinue={() => {
                        
                        setEmployeeType(changeTypeTo);
                        // setNewEmployeeRecord({
                        //     ...newEmployeeRecord,
                        //     employeeType: changeTypeTo,
                        // })
                        resetEmployeeRecordCustomProperties(changeTypeTo);
                        setValidateChangeType(false);
                        setChangeTypeTo(undefined);
                        setHasChanges(true);
                    }}
                />
            }
            
            {hasChanges &&
                <AppChanges 
                    submitText={id ? "Update" : "Add Employee"}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                />
            }
        </div>
    )
}